import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { distinctUntilChanged, map, Observable } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  PredefinedDateTimeOperator,
  PredefinedRangedDateTimeSchema,
} from '@blaze-shared/datasets-domain';
import { BlazeSchema } from '@blaze-shared/schema-domain';
import { convertDateToISO, convertEpochToISO, isDeepEqual } from '@blaze-shared/shared-util';

import { DataProviderQuery, InputDataPackage } from '../../io-data.models';

import { DataSourceProvider } from './data-source.data-provider';

@Injectable({ providedIn: 'root' })
export class RangedDateDataProvider implements DataSourceProvider {
  private readonly store = inject(Store);
  readonly sourceType = 'column-filter-ranged' as const;

  getDataStream({ source }: DataProviderQuery): Observable<InputDataPackage> {
    return this.store.select(runtimeSelectors.selectWorkflowById(source.id)).pipe(
      map(() => {
        const schema: BlazeSchema = PredefinedRangedDateTimeSchema;
        const rangedFilterOptions: {
          label: string;
          value: {
            startDate: string;
            endDate: string;
            startDateTime: string;
            endDateTime: string;
          };
        }[] = [];

        const predefinedOperators = source.predefinedFilterOperators || [];

        for (const operator of predefinedOperators) {
          const now = Date.now();
          let startDate: Date;
          let endDate: Date;

          // Each of these has an end-date on purpose. We cannot leave end date undefined in cases where a date is
          // specified sometime in the future from current now. In such cases, open-ended query would incorrectly
          // return rows with dates set to future dates.
          switch (operator) {
            case PredefinedDateTimeOperator.CurrentWeek:
              startDate = startOfWeek(now);
              rangedFilterOptions.push({
                label: operator,
                value: {
                  startDate: convertDateToISO(startDate, 'date'),
                  endDate: convertEpochToISO(now, 'date'),
                  startDateTime: convertDateToISO(startDate, 'utc'),
                  endDateTime: convertEpochToISO(now, 'utc'),
                },
              });
              break;
            case PredefinedDateTimeOperator.Last12Months:
              startDate = subYears(now, 1);
              rangedFilterOptions.push({
                label: operator,
                value: {
                  startDate: convertDateToISO(startDate, 'date'),
                  endDate: convertEpochToISO(now, 'date'),
                  startDateTime: convertDateToISO(startDate, 'utc'),
                  endDateTime: convertEpochToISO(now, 'utc'),
                },
              });
              break;
            case PredefinedDateTimeOperator.LastMonth:
              startDate = startOfMonth(subMonths(now, 1));
              endDate = endOfMonth(subMonths(now, 1));
              rangedFilterOptions.push({
                label: operator,
                value: {
                  startDate: convertDateToISO(startDate, 'date'),
                  endDate: convertDateToISO(endDate, 'date'),
                  startDateTime: convertDateToISO(startDate, 'utc'),
                  endDateTime: convertDateToISO(endDate, 'utc'),
                },
              });
              break;
            case PredefinedDateTimeOperator.LastWeek:
              startDate = startOfWeek(subWeeks(now, 1));
              endDate = endOfWeek(subWeeks(now, 1));
              rangedFilterOptions.push({
                label: operator,
                value: {
                  startDate: convertDateToISO(startDate, 'date'),
                  endDate: convertDateToISO(endDate, 'date'),
                  startDateTime: convertDateToISO(startDate, 'utc'),
                  endDateTime: convertDateToISO(endDate, 'utc'),
                },
              });
              break;
            case PredefinedDateTimeOperator.LastYear:
              startDate = startOfYear(subYears(now, 1));
              endDate = endOfYear(subYears(now, 1));
              rangedFilterOptions.push({
                label: operator,
                value: {
                  startDate: convertDateToISO(startDate, 'date'),
                  endDate: convertDateToISO(endDate, 'date'),
                  startDateTime: convertDateToISO(startDate, 'utc'),
                  endDateTime: convertDateToISO(endDate, 'utc'),
                },
              });
              break;
            case PredefinedDateTimeOperator.MonthToDate:
              startDate = startOfMonth(now);
              rangedFilterOptions.push({
                label: operator,
                value: {
                  startDate: convertDateToISO(startDate, 'date'),
                  endDate: convertEpochToISO(now, 'date'),
                  startDateTime: convertDateToISO(startDate, 'utc'),
                  endDateTime: convertEpochToISO(now, 'utc'),
                },
              });
              break;
            case PredefinedDateTimeOperator.YearToDate:
              startDate = startOfYear(now);
              rangedFilterOptions.push({
                label: operator,
                value: {
                  startDate: convertDateToISO(startDate, 'date'),
                  endDate: convertEpochToISO(now, 'date'),
                  startDateTime: convertDateToISO(startDate, 'utc'),
                  endDateTime: convertEpochToISO(now, 'utc'),
                },
              });
              break;
            default:
              break;
          }
        }

        return {
          sourceType: this.sourceType,
          data: rangedFilterOptions,
          schema,
        };
      }),
      distinctUntilChanged(isDeepEqual),
    );
  }
}
